<template>
  <v-text-field
      :class="sliderClass"
      :prepend-icon="withDecreaseButton ? 'mdi-minus' : null"
      :append-outer-icon="withIncreaseButton ? 'mdi-plus' : null"
      v-model="model"
      solo
      outlined
      flat
      dense
      @blur="blur"
      :error-messages="errorMessages"
      @click:append-outer="increase"
      @click:prepend="decrease"
      v-bind="vuetifyProps"
  >
  </v-text-field>
</template>

<script>
  import {ComponentWithModelMixin} from "../../mixins/FlowGenerator";
  import {round} from "../../utils"

  export default {

    name: "SliderWithButton",
    props: {
      amountOfDecimals: {
        type: Number,
        default: 0
      },
      disableTextInput: {
        type: Boolean,
        description: "No free text input, only allow input with increment buttons",
        default: false
      },
      min: {
        type: Number,
        description: "The minimum value of the slider",
        default: 1
      },
      max: {
        type: Number,
        description: "The maximum value of the slider",
        default: 100
      },
      increment: {
        type: Number,
        default: 1
      },
      errorMessages: {
        type: Array,
        default: () => ([])
      },
      vuetifyProps: {
        type: Object,
        default: () => ({}),
        description: "Bind this object data to props of vuetify component. See property specification for used component in vuetify docs."
      }
    },
    data() {
      return {
        model: 0,
        updatedIncrement: 0
      }
    },
    watch: {
      'min': 'updateModel',
      'max': 'updateModel',
      'increment': 'updateModel'
    },
    mixins: [
      ComponentWithModelMixin
    ],
    computed: {
      sliderClass() {
        return this.disableTextInput
          ? 'slider-with-buttons disable-text-input'
          : 'slider-with-buttons'
      },
      roundedMin() {
        return round(this.min, this.amountOfDecimals)
      },
      roundedMax() {
        return round(this.max, this.amountOfDecimals)
      },
      withIncreaseButton() {
        return this.updatedIncrement < this.roundedMax
      },
      withDecreaseButton() {
        return this.updatedIncrement > this.roundedMin
      }
    },
    methods: {
      async updateModel() {
        // wait until the new default is set on the dom element, to prevent
        // the default from overriding the new setModelToNearestIncrement value
        await this.$nextTick()
        // update the model on increment change to round to the new increment
        this.setModelToNearestIncrement()
      },
      setModelToNearestIncrement(modifier=0) {
        /* Set the model to the nearest increment, make sure always use
         * the same calculation for this to prevent rounding errors.
         * The modifier can be used to add or subtract increments.
         */

        // get the amount of increments that should be added to the
        // minimum value of the slider, based on the model and the modifier
        const amountOfIncrementsFromMin =
          round((this.model - this.min) / this.increment) + modifier
        // add the minimum value of the slider to the calculated increment
        // value, and round this value
        const incrementedModelValue =
          round(
            (amountOfIncrementsFromMin * this.increment) + this.min,
            this.amountOfDecimals
          )
        // calculate min and max values for checking if the incremented
        // value is close (within half an increment) to min and max
        const nearMin = round(
          this.min + (this.increment / 2),
          this.amountOfDecimals
        )
        const nearMax = round(
          this.max - (this.increment / 2),
          this.amountOfDecimals
        )

        // now check to which value this.model should be set
        if (incrementedModelValue < nearMin) {
          // calculated value is close to or below rounded min, set model to min
          this.model = this.roundedMin
        } else if (incrementedModelValue > nearMax) {
          // calculated value is close to or above max, set model to max
          this.model = this.roundedMax
        } else {
          // set the calculated value
          this.model = incrementedModelValue
        }

        // set updatedIncrement value for showing or hiding the increase and decrease
        // button, we need a separate value for this because model is always updated
        // on user input (ignoring the blur event)
        this.updatedIncrement = this.model

        // always emit a change afer updating the model
        this.$emit('change')

      },
      blur() {
        // update the model on blur to round to the new increment
        this.setModelToNearestIncrement()
      },
      increase() {
        this.setModelToNearestIncrement(1)
      },
      decrease() {
        this.setModelToNearestIncrement(-1)
      },
      loadModel() {
        if (this.value !== undefined) {
          this.model = this.value
        }
      }
    }
  }
</script>

<style lang="scss">

  .slider-with-buttons.v-text-field.disable-text-input {
    .v-input__slot {
      pointer-events: none;
      background-color: #ddd !important;
    }
  }

  .slider-with-buttons.v-text-field {

    &.v-text-field--enclosed:not(.v-text-field--rounded) >.v-input__control>.v-input__slot {
      padding: 0;
      background-color: lightgray;
    }
    .v-input__prepend-outer, .v-input__append-outer {
      background-color: lightgray;
    }
    .v-text-field__slot {
      justify-content: center;
    }
    .v-text-field__suffix {
      flex: 0 1 auto;
      width: 90%;
    }
    input {
      flex: 0 1 auto;
      text-align: right;
    }
  }

</style>
